import React, { useEffect, useState } from 'react';

import mapboxgl, { ExpressionSpecification, LngLatBoundsLike } from 'mapbox-gl';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PublicIcon from '@mui/icons-material/Public';
import MyLocationIcon from '@mui/icons-material/MyLocation';

import {
  default as MapAlias,
  Layer,
  LayerProps,
  MapRef,
  Source,
} from 'react-map-gl/mapbox';
import { bbox } from '@turf/bbox';

import {
  Theme,
  useTheme,
  Box,
  Button,
  ButtonGroup,
  Stack,
  ButtonBase,
  useMediaQuery,
  Snackbar,
  Alert,
  Slide,
  SlideProps,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Asset, isAsset } from '@/types/asset_search';
import { AssetTypeEnum } from '@/types/enums';
import {
  CHECKED_CLICKED_FOUND_ICON,
  CHECKED_CLICKED_ICON,
  CHECKED_FOUND_ICON,
  CHECKED_ICON,
  CLICKED_FOUND_ICON,
  CLICKED_ICON,
  DEFAULT_FOUND_ICON,
  DEFAULT_ICON,
  ImageMap,
  setupCustomImages,
} from './MapboxIcons';
import { getConfig } from '@/constants';
import CircleRangeIndicator from './CircleRangeIndicator';
import { distanceBetweenTwoPoints } from '@/utils';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  setSatelliteView,
  setTetheredEnabledGlobal,
  setUserPositionGlobal,
} from '@/features/user/userSlice';
import CircularProgress from '@mui/material/CircularProgress';

let imagesMap: Map<MapSourceType, ImageMap[]> = new Map();
imagesMap = setupCustomImages();

interface Props {
  data: Asset[];
  items: any;
  selected?: Asset | null;
  checked?: Asset[] | null;
  setSelected?: any;
  mapSrcType?: MapSourceType;
  bestFitZoom?: boolean;
  customPanCenter?: { lat: number; lon: number };
  overwriteClusterMaxZoom?: number;
  resized?: number[];
  mapRef: any;
}
const CLUSTER_MAX_ZOOM = 17;

export const getUserToAssetLine = (theme: Theme): LayerProps => {
  return {
    id: `layer-userToAssetLine`,
    type: 'line',
    paint: {
      'line-color': theme.palette.primary.main,
      'line-opacity': 1,
      'line-width': 4,
    },
  };
};

export const getLabelLayer = (
  id: string | number,
  name: string,
): LayerProps => {
  return {
    id: `symb-${id}`,
    type: 'symbol',
    layout: {
      'symbol-placement': 'point',
      'text-max-width': 4,
      'text-field': name,
      'text-anchor': 'center',
      'text-justify': 'center',
      'text-allow-overlap': false,
    },
    paint: {
      'text-halo-color': 'white',
      'text-halo-width': 2,
    },
  };
};

const clusterLayer: LayerProps = {
  id: 'clusters',
  type: 'circle',
  source: 'mapoints',
  filter: ['has', 'point_count'],
  paint: {
    'circle-color': [
      'step',
      ['get', 'point_count'],
      '#104ED6',
      100,
      '#AA56D6',
      750,
      '#ED716B',
    ],
    'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
    'circle-stroke-color': 'white',
    'circle-stroke-width': 1.5,
  },
};

const clusterCountLayer: LayerProps = {
  id: 'cluster-count',
  type: 'symbol',
  source: 'mapoints',
  filter: ['has', 'point_count'],
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-size': 12,
  },
  paint: {
    'text-color': 'white',
  },
};

let positionWatcher: any;

const getUnclusteredPointLayer = (
  selected: Asset | null | undefined,
  checked: Asset[] | null | undefined,
): LayerProps => {
  const comparatorKey = 'asset_uuid';

  const checkedUuids = checked
    ? checked.map((el) => el[comparatorKey as keyof typeof el])
    : [];

  const clicked_xp: ExpressionSpecification = [
    '==',
    ['get', comparatorKey],

    selected ? selected[comparatorKey as keyof typeof selected] : '',
  ];

  const checked_xp: ExpressionSpecification = [
    'in',
    ['get', comparatorKey],
    ['literal', checkedUuids],
  ];

  const marked_as_found_xp: ExpressionSpecification = [
    '==',
    ['get', 'markedAsFound'],
    true,
  ];

  const icon_image_xp: ExpressionSpecification = [
    'case',
    ['all', marked_as_found_xp, clicked_xp, checked_xp],
    CHECKED_CLICKED_FOUND_ICON,
    ['all', marked_as_found_xp, checked_xp],
    CHECKED_FOUND_ICON,
    ['all', marked_as_found_xp, clicked_xp],
    CLICKED_FOUND_ICON,
    ['all', marked_as_found_xp],
    DEFAULT_FOUND_ICON,
    ['all', clicked_xp, checked_xp],
    CHECKED_CLICKED_ICON,
    checked_xp,
    CHECKED_ICON,
    clicked_xp,
    CLICKED_ICON,
    DEFAULT_ICON,
  ];

  return {
    id: 'unclustered-point',
    type: 'symbol',
    source: 'mapoints',
    filter: ['!', ['has', 'point_count']],
    layout: {
      'icon-image': icon_image_xp,
      'icon-size': 1,
      'icon-allow-overlap': true,
      'symbol-avoid-edges': true,
    },
  };
};

export type MapSourceType = AssetTypeEnum;

const Mapbox = (props: Props) => {
  const theme = useTheme<Theme>();

  const {
    siteMetadata,
    userPosition: userPositionGlobal,
    tetheredEnabled: tetheredEnabledGlobal,
    satelliteView,
  } = useAppSelector((state) => state.user);

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const isDesktop = useMediaQuery('(min-width:900px)');

  const [showMapIsReadyLabel, setShowMapIsReadyLabel] =
    useState<boolean>(false);
  const [tetheringIsEnabled, setTetheringIsEnabled] = useState<boolean>(
    tetheredEnabledGlobal,
  );
  const [userPositionLoading, setUserPositionLoading] =
    useState<boolean>(false);
  const [snackBarOpen, setSnackBarOpen] = useState<any>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [userPosition, setUserPosition] = useState<GeolocationPosition>({
    coords: {
      accuracy: 0,
      altitude: null,
      heading: null,
      speed: null,
      altitudeAccuracy: null,
      latitude: userPositionGlobal?.lat ?? 0,
      longitude: userPositionGlobal?.lon ?? 0,
    },
    timestamp: 0,
  } as GeolocationPosition);
  const [userLocationAcquired, setUserLocationAcquired] =
    useState<boolean>(false);

  const getClusterMaxZoom = () => {
    if (props?.overwriteClusterMaxZoom) {
      return props.overwriteClusterMaxZoom;
    }
    return CLUSTER_MAX_ZOOM;
  };

  const mapPointLocs: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    type: 'FeatureCollection',
    features: [],
  };

  props?.data?.forEach((a: any) => {
    if (a.lon != 'NULL' && a.lat) {
      const point: GeoJSON.Feature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [a.lon, a.lat],
        },
        properties: a,
      };
      mapPointLocs.features.push(point);
    }
  });

  const zones: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    type: 'FeatureCollection',
    features: [],
  };

  if (props?.items) {
    Object.values(props.items).forEach((a: any) => {
      if (a?.geoJson && a?.geoJson?.geometry) {
        const poly: GeoJSON.Feature = {
          type: 'Feature',
          geometry: a.geoJson.geometry,
          properties: {
            'fill-color': a?.appearance?.fillColor,
            'outline-color':
              a.appearance?.outlineColor == a.appearance?.fillColor
                ? '#000'
                : a.appearance?.outlineColor,
            name: a.name,
          },
        };
        zones.features.push(poly);
      }
    });
  }

  if (!siteMetadata.mapboxToken) return null;

  const getMapStyle = () => {
    if (satelliteView) {
      return 'mapbox://styles/mapbox/satellite-v9';
    }
    return theme.palette.mode == 'dark'
      ? 'mapbox://styles/mapbox/dark-v10'
      : siteMetadata.mapboxStyle;
  };

  const getUserToAssetDistance = () => {
    return distanceBetweenTwoPoints(
      {
        lat: userPosition?.coords?.latitude,
        lon: userPosition.coords?.longitude,
      },
      { lat: props.selected?.lat || 0, lon: props.selected?.lon || 0 },
    );
  };

  const easeToCoords = (map: MapRef, lon: number, lat: number) => {
    map.easeTo({
      center: [lon, lat],
      zoom:
        map.getZoom() > getClusterMaxZoom() + 1
          ? map.getZoom()
          : getClusterMaxZoom() + 1,
    });
  };

  useEffect(() => {
    if (tetheredEnabledGlobal) {
      if (positionWatcher != null && navigator?.geolocation?.clearWatch) {
        navigator.geolocation.clearWatch(positionWatcher);
        positionWatcher = null;
      }
      navigator.geolocation.getCurrentPosition(
        watchPosition,
        () => () => {
          return;
        },
        {
          enableHighAccuracy: true,
          maximumAge: 30000,
          timeout: 27000,
        },
      );

      setTetheringIsEnabled(true);
    }

    if (props.bestFitZoom == true && !tetheringIsEnabled) {
      const bb = bbox(mapPointLocs);
      if (!bb.includes(Infinity)) {
        reboundMap([
          [bb[0], bb[1]],
          [bb[2], bb[3]],
        ]);
      }
    }
  }, []);

  useEffect(() => {
    if (getConfig().IS_PLAYWRIGHT) {
      setShowMapIsReadyLabel(false);
    }
    if (
      isDesktop &&
      props.selected &&
      props.selected?.lat &&
      props.selected?.lon
    ) {
      if (props.mapRef?.current && !tetheringIsEnabled) {
        const map: MapRef = props.mapRef.current;
        easeToCoords(map, props.selected?.lon, props.selected?.lat);
      }
    }
  }, [props.selected]);

  useEffect(() => {
    const userLat = userPosition?.coords?.latitude;
    const userLon = userPosition?.coords?.longitude;

    if (
      userLat &&
      userLon &&
      props.selected?.lat &&
      props?.selected?.lon &&
      tetheringIsEnabled
    ) {
      // reboundMap([
      //   [userLon, userLat],
      //   [props?.selected?.lon, props?.selected?.lat],
      // ]);
      if (userPositionGlobal !== null) {
        setUserPositionLoading(false);
      } else {
        // reset user position
        setUserPosition({
          coords: {
            accuracy: 0,
            altitude: null,
            heading: null,
            speed: null,
            altitudeAccuracy: null,
            latitude: 0,
            longitude: 0,
          },
          timestamp: 0,
        } as GeolocationPosition);
      }
    }
  }, [props.selected, userLocationAcquired, tetheringIsEnabled]);

  useEffect(() => {
    if (props.mapRef?.current && props?.resized) {
      const map: MapRef = props.mapRef.current;
      map.resize();
    }
  }, [props?.resized]);
  const reboundMap = (bounds: LngLatBoundsLike) => {
    if (props.mapRef?.current) {
      const map: MapRef = props.mapRef.current;
      map.fitBounds(bounds, { padding: 50, maxZoom: getClusterMaxZoom() + 1 });
    }
  };

  const onUserLocationToggle = async () => {
    if (!tetheringIsEnabled) {
      if (positionWatcher != null && navigator?.geolocation?.clearWatch) {
        navigator.geolocation.clearWatch(positionWatcher);
        positionWatcher = null;
      }
      navigator.geolocation.getCurrentPosition(
        watchPosition,
        () => () => {
          return;
        },
        {
          enableHighAccuracy: true,
          maximumAge: 30000,
          timeout: 27000,
        },
      );

      setTetheringIsEnabled(true);
      dispatch(setTetheredEnabledGlobal(true));
      setUserPositionLoading(true);
    } else {
      if (positionWatcher != null && navigator?.geolocation?.clearWatch) {
        navigator.geolocation.clearWatch(positionWatcher);
        positionWatcher = null;
      }
      setTetheringIsEnabled(false);
      dispatch(setUserPositionGlobal(null));
      dispatch(setTetheredEnabledGlobal(false));
      setUserPositionLoading(false);
    }
  };

  const onIdle = () => {
    if (getConfig().IS_PLAYWRIGHT) {
      setShowMapIsReadyLabel(true);
    }
  };
  const onLoad = () => {
    if (props?.selected && props.selected?.lat && props.selected?.lon) {
      if (props.mapRef?.current) {
        easeToCoords(
          props.mapRef.current,
          props.selected?.lon,
          props.selected?.lat,
        );
      }
    }
  };

  const SlideLeftTransition = (props: SlideProps) => {
    return <Slide {...props} direction="left" />;
  };

  const zoomIn = () => {
    if (props.mapRef?.current) {
      const map: MapRef = props.mapRef.current;
      map.zoomIn();
    }
  };
  const zoomOut = () => {
    if (props.mapRef?.current) {
      const map: MapRef = props.mapRef.current;
      map.zoomOut();
    }
  };

  const actualRenderZones = () => {
    let beforeIdProp = {};
    if (mapPointLocs.features.length) {
      beforeIdProp = { beforeId: 'clusters' };
    }
    return (
      <Source key="mapzones" type="geojson" data={zones}>
        <Layer
          {...beforeIdProp}
          {...{
            id: 'data',
            type: 'fill',
            paint: {
              'fill-color': ['get', 'fill-color'],
            },
          }}
        />
        <Layer
          {...beforeIdProp}
          {...{
            type: 'line',
            paint: {
              'line-color': ['get', 'outline-color'],
              'line-width': 1,
            },
          }}
        />
        <Layer
          {...beforeIdProp}
          {...{
            type: 'symbol',
            layout: {
              'symbol-placement': 'point',
              'text-max-width': 4,
              'text-field': ['get', 'name'],
              'text-anchor': 'center',
              'text-justify': 'center',
              'text-allow-overlap': false,
            },
            paint: {
              'text-halo-color': 'white',
              'text-halo-width': 2,
            },
          }}
        />
      </Source>
    );
  };

  const onStyleData = () => {
    const mapSourceType =
      props?.mapSrcType || siteMetadata.assetType || AssetTypeEnum.CAR;
    const theImageArray = imagesMap.get(mapSourceType);
    const map = props.mapRef?.current?.getMap();
    if (map && theImageArray) {
      theImageArray.forEach((keyImg: ImageMap) => {
        const key = keyImg.key;
        if (!map.hasImage(key)) {
          map.addImage(key, keyImg.img);
        }
      });
    }
  };

  const onClick = (event: mapboxgl.MapLayerMouseEvent) => {
    if (event?.features) {
      const feature = event.features[0];
      if (feature?.layer?.id == 'unclustered-point' && props?.setSelected) {
        props.setSelected(feature.properties);
      }
    }
  };

  const handleSnackBarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackBarOpen(false);
  };

  const onPositionUpdate = (newPosition: GeolocationPosition) => {
    const newUserLat = newPosition?.coords?.latitude;
    const newUserLon = newPosition?.coords?.longitude;
    const newUserAcc = newPosition?.coords?.accuracy;
    const currentPositionLat = userPosition.coords?.latitude;
    const currentPositionLong = userPosition.coords?.longitude;
    const currentPositionAcc = userPosition.coords?.accuracy;

    if (newUserLat && newUserLon && newUserLat != 0 && newUserLon != 0) {
      if (
        Math.abs(currentPositionLat - newUserLat) > 0.00001 ||
        Math.abs(currentPositionLong - newUserLon) > 0.00001 ||
        (currentPositionAcc != null &&
          newUserAcc != null &&
          Math.abs(currentPositionAcc - newUserAcc) > 1)
      ) {
        setUserPosition(newPosition);
        dispatch(
          setUserPositionGlobal({
            lat: newPosition.coords.latitude,
            lon: newPosition.coords.longitude,
          }),
        );
        setUserLocationAcquired(true);
      }
    }
  };

  const watchPosition = (userPosition: GeolocationPosition) => {
    if (navigator.geolocation == null) {
      return;
    }

    if (positionWatcher != null && navigator?.geolocation?.clearWatch) {
      navigator.geolocation.clearWatch(positionWatcher);
    }
    setUserPositionLoading(false);
    setUserPosition(userPosition);
    dispatch(
      setUserPositionGlobal({
        lat: userPosition.coords.latitude,
        lon: userPosition.coords.longitude,
      }),
    );

    positionWatcher = navigator.geolocation.watchPosition(
      (pos) => onPositionUpdate(pos),
      () => () => {
        return;
      },
      {
        enableHighAccuracy: true,
        maximumAge: 30000,
        timeout: 27000,
      },
    );
  };

  return (
    <>
      <Snackbar
        open={snackBarOpen.open}
        autoHideDuration={6000}
        onClose={handleSnackBarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          zIndex: 10,
          top: {
            xs: '70px!important',
            sm: '100px!important',
            md: '100px!important',
          },
        }}
        TransitionComponent={SlideLeftTransition}
      >
        <Alert
          onClose={handleSnackBarClose}
          severity={snackBarOpen.severity}
          sx={{ width: '100%' }}
        >
          {snackBarOpen.message}
        </Alert>
      </Snackbar>
      {showMapIsReadyLabel && (
        <Box data-testid="map-is-ready-label">Map is ready</Box>
      )}
      <MapAlias
        ref={props.mapRef}
        initialViewState={{
          bounds: [siteMetadata.mapboxSW, siteMetadata.mapboxNE],
        }}
        mapStyle={getMapStyle()}
        interactiveLayerIds={
          mapPointLocs.features.length ? ['unclustered-point', 'clusters'] : []
        }
        onClick={onClick}
        onIdle={onIdle}
        onLoad={onLoad}
        onStyleData={onStyleData}
        mapboxAccessToken={siteMetadata.mapboxToken}
        data-testid="mapbox-element"
      >
        {mapPointLocs.features.length && (
          <Source
            key="mapoints"
            type="geojson"
            data={mapPointLocs}
            cluster={true}
            clusterMaxZoom={getClusterMaxZoom()}
            clusterRadius={50}
          >
            <Layer {...clusterLayer} />
            <Layer {...clusterCountLayer} />
            <Layer
              {...getUnclusteredPointLayer(props.selected, props.checked)}
            />
          </Source>
        )}
        {zones.features.length && actualRenderZones()}
        {isAsset(props?.selected) &&
          props.mapRef?.current &&
          (props.mapRef.current as any)?.getZoom() >=
            getClusterMaxZoom() + 1 && (
            <CircleRangeIndicator
              lat={props?.selected?.lat}
              lon={props?.selected?.lon}
            />
          )}
        {tetheringIsEnabled &&
          props?.selected?.lon &&
          props?.selected?.lat &&
          userPosition.coords.latitude != 0 &&
          userPosition.coords.longitude != 0 && (
            <>
              <Source
                type="geojson"
                data={{
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    type: 'LineString',
                    coordinates: [
                      [
                        userPosition?.coords?.longitude,
                        userPosition?.coords?.latitude,
                      ],
                      [props?.selected?.lon, props?.selected?.lat],
                    ],
                  },
                }}
              >
                <Layer
                  id="userLocation"
                  key="userLocation"
                  type="circle"
                  paint={{
                    'circle-color': '#4C82F7',
                    'circle-radius': {
                      base: 1.5,
                      stops: [
                        [1, 1],
                        [6, 3],
                        [10, 8],
                        [22, 12],
                      ],
                    },
                  }}
                />
                <Layer {...getUserToAssetLine(theme)} />
              </Source>
              <CircleRangeIndicator
                lat={userPosition?.coords?.latitude}
                lon={userPosition?.coords?.longitude}
                range={userPosition?.coords?.accuracy}
              />
            </>
          )}
      </MapAlias>
      {tetheringIsEnabled &&
        props?.selected &&
        userPosition.coords.latitude != 0 &&
        userPosition.coords.longitude != 0 && (
          <Box
            sx={{
              zIndex: 100,
              position: 'absolute',
              top: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: theme.palette.background.default,
              borderRadius: '15px',
              height: '32px',
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              padding: '5px 10px',
              fontSize: '12px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px 0 rgba(0,0,0,0.2)',
            }}
          >
            {t('mapbox.distancePill')}: {getUserToAssetDistance().toFixed(0)} ft
          </Box>
        )}
      <span style={{ right: 5, top: 20, position: 'absolute' }}>
        <Stack
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-end"
          spacing={1}
        >
          {isDesktop && (
            <ButtonGroup
              sx={(theme) => ({
                borderRadius: '12px',
                width: '40px!important',
                height: '73px!important',
                minWidth: '40px',
                padding: 0,
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.background.default,
              })}
              orientation="vertical"
              variant="text"
            >
              <Button data-testid="zoom-in-button" onClick={zoomIn}>
                <AddIcon />
              </Button>
              <Button data-testid="zoom-out-button" onClick={zoomOut}>
                <RemoveIcon />
              </Button>
            </ButtonGroup>
          )}
          <ButtonBase
            sx={(theme) => ({
              borderRadius: '28px',
              width: '40px!important',
              height: '40px!important',
              minWidth: '40px',
              padding: 0,
              color: theme.palette.primary.main,
              backgroundColor: theme.palette.background.default,
            })}
            data-testid="satellite-view-button"
            onClick={() => dispatch(setSatelliteView(!satelliteView))}
          >
            <PublicIcon fontSize="small" />
          </ButtonBase>
          {props?.selected && props?.selected?.lat && props?.selected?.lon && (
            <ButtonBase
              sx={(theme) => ({
                borderRadius: '28px',
                width: '40px!important',
                height: '40px!important',
                minWidth: '40px',
                marginLeft: '5px',
                color:
                  tetheringIsEnabled && !userPositionLoading
                    ? 'white'
                    : theme.palette.text.primary,
                backgroundColor:
                  tetheringIsEnabled && !userPositionLoading
                    ? theme.palette.primary.main
                    : 'white',
              })}
              data-testid="user-position-button"
              onClick={onUserLocationToggle}
            >
              {userPositionLoading ? (
                <CircularProgress size={20} />
              ) : (
                <MyLocationIcon fontSize="small" />
              )}
            </ButtonBase>
          )}
        </Stack>
      </span>
    </>
  );
};

export default Mapbox;
